import {
  IconAlertTriangle,
  IconInbox,
  IconInfoCircle,
  TablerIcon,
} from "@tabler/icons-react";
import { HTMLAttributes, ReactNode } from "react";
import { __ } from "@common/helpers/i18n";
import { EmptyStatePrimitive } from "@app/components/ui/empty-state";
import { cn } from "@app/lib/utils";
import { Spinner } from "../loading/Spinner";
import { FooterActions } from "./ActionGroup";
import { cardSurfaceClassName } from "./Card";

export const stateToneClassName = {
  default: "bg-primary/10 text-primary ring-primary/10",
  danger: "bg-destructive/10 text-destructive ring-destructive/15",
  warning: "bg-warning/10 text-warning ring-warning/20",
  muted: "bg-muted/60 text-muted-foreground ring-border",
  success: "bg-success/10 text-success ring-success/15",
};

export type StateProps = {
  action?: ReactNode;
  className?: string;
  description?: ReactNode;
  icon?: ReactNode;
  secondaryAction?: ReactNode;
  size?: "page" | "section" | "compact";
  surface?: "card" | "subtle" | "plain";
  title?: ReactNode;
  tone?: keyof typeof stateToneClassName;
  visual?: "object" | "icon" | "custom";
};

export type StateViewStatus =
  | "empty"
  | "error"
  | "info"
  | "loading"
  | "success";

export type StateViewProps = Omit<StateProps, "icon" | "tone" | "visual"> & {
  icon?: ReactNode;
  status?: StateViewStatus;
  tone?: StateProps["tone"];
  visual?: StateProps["visual"];
};

const iconSizeClassName = {
  page: "mb-4 h-14 w-14 [&_svg]:h-7 [&_svg]:w-7",
  section: "mb-3 h-12 w-12 [&_svg]:h-6 [&_svg]:w-6",
  compact: "mb-2.5 h-10 w-10 [&_svg]:h-5 [&_svg]:w-5",
};

const surfaceClassName = {
  card: cn(cardSurfaceClassName, "bg-card"),
  subtle: "rounded-lg border border-border bg-muted/20",
  plain: "",
};

const pageCardStateClassName =
  "mx-auto min-h-[320px] w-full max-w-5xl flex-none px-5 py-10 sm:px-6 sm:py-12 md:min-h-[360px] md:px-8 md:py-14";

export const statePageSectionClassName =
  "flex min-h-0 flex-1 items-start justify-center px-4 py-6 md:px-5 xl:px-6";

export function StatePage({
  children,
  className,
  ...props
}: HTMLAttributes<HTMLElement>) {
  return (
    <section className={cn(statePageSectionClassName, className)} {...props}>
      {children}
    </section>
  );
}

const objectSizeClassName = {
  page: {
    root: "mb-7 h-36 w-44",
    backPaper: "top-5 h-28 w-28",
    frontPaper: "top-1 h-32 w-32",
    icon: "h-20 w-20 [&_svg]:h-14 [&_svg]:w-14",
  },
  section: {
    root: "mb-6 h-32 w-40",
    backPaper: "top-5 h-24 w-24",
    frontPaper: "top-1 h-28 w-28",
    icon: "h-[4.5rem] w-[4.5rem] [&_svg]:h-12 [&_svg]:w-12",
  },
  compact: {
    root: "mb-5 h-28 w-32",
    backPaper: "top-5 h-20 w-20",
    frontPaper: "top-1 h-24 w-24",
    icon: "h-14 w-14 [&_svg]:h-10 [&_svg]:w-10",
  },
};

function StateObjectVisual({
  icon,
  size,
}: {
  icon: ReactNode;
  size: NonNullable<StateProps["size"]>;
}) {
  const visualSize = objectSizeClassName[size];

  return (
    <div
      aria-hidden="true"
      className={cn("relative isolate shrink-0", visualSize.root)}
    >
      <div
        className={cn(
          "absolute start-1/2 -translate-x-[58%] rounded-xl border border-border bg-muted/20 shadow-sm rtl:translate-x-[58%]",
          visualSize.backPaper,
        )}
      />
      <div
        className={cn(
          "absolute start-1/2 -translate-x-[42%] rounded-xl border border-border bg-card shadow-soft rtl:translate-x-[42%]",
          visualSize.frontPaper,
        )}
      >
        <div className="absolute start-4 top-4 flex gap-1.5">
          <span className="h-1.5 w-1.5 rounded-full bg-primary/35" />
          <span className="h-1.5 w-1.5 rounded-full bg-border" />
          <span className="h-1.5 w-1.5 rounded-full bg-border" />
        </div>
        <div className="absolute inset-0 flex items-center justify-center pt-5">
          <div
            className={cn(
              "flex items-center justify-center text-muted-foreground/25 [&_svg]:stroke-[1.7]",
              visualSize.icon,
            )}
          >
            {icon}
          </div>
        </div>
      </div>
    </div>
  );
}

function LoadingStateVisual({
  rows = 3,
  showRows = true,
  size,
}: {
  rows?: number;
  showRows?: boolean;
  size: NonNullable<StateProps["size"]>;
}) {
  const spinnerSize = size === "page" ? "xl" : size === "section" ? "lg" : "md";
  const rowCount = Math.max(1, Math.min(rows, size === "compact" ? 2 : 4));

  return (
    <div aria-hidden="true" className="mb-4 flex flex-col items-center">
      <span className="flex h-14 w-14 items-center justify-center rounded-md bg-primary/10 ring-1 ring-primary/10">
        <Spinner size={spinnerSize} />
      </span>
      {showRows && (
        <div className="mt-4 w-56 max-w-[70vw] space-y-2">
          {Array.from({ length: rowCount }).map((_, index) => (
            <span
              key={index}
              className={cn(
                "mx-auto block h-2 animate-pulse rounded-full bg-muted/80",
                index === 0
                  ? "w-full"
                  : index === rowCount - 1
                    ? "w-2/3"
                    : "w-5/6",
              )}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function StateShell({
  action,
  className,
  description,
  icon,
  secondaryAction,
  size = "page",
  surface = "card",
  title,
  tone = "default",
  visual = "icon",
}: StateProps) {
  return (
    <EmptyStatePrimitive
      className={cn(
        surfaceClassName[surface],
        className,
        surface === "card" && size === "page" && pageCardStateClassName,
      )}
      size={size}
    >
      {icon && visual === "custom" ? (
        icon
      ) : icon && visual === "object" ? (
        <StateObjectVisual icon={icon} size={size} />
      ) : icon ? (
        <div
          className={cn(
            "flex items-center justify-center rounded-md ring-1 [&_svg]:stroke-[2]",
            iconSizeClassName[size],
            stateToneClassName[tone],
          )}
        >
          {icon}
        </div>
      ) : null}
      {title !== undefined && title !== null && (
        <h3
          className={cn(
            "font-bold text-foreground",
            size === "compact" ? "text-sm leading-6" : "text-base leading-7",
          )}
        >
          {title}
        </h3>
      )}
      {description && (
        <p
          className={cn(
            "mt-1.5 max-w-md leading-6 text-muted-foreground",
            size === "compact" ? "text-xs" : "text-sm",
          )}
        >
          {description}
        </p>
      )}
      {(action || secondaryAction) && (
        <FooterActions align="center" className="mt-5">
          {action}
          {secondaryAction}
        </FooterActions>
      )}
    </EmptyStatePrimitive>
  );
}

export function EmptyState({
  surface = "card",
  title = __("No data to display"),
  ...props
}: Omit<StateProps, "tone">) {
  return (
    <StateView status="empty" surface={surface} title={title} {...props} />
  );
}

export function ErrorState({
  description = __(
    "The page could not be loaded. Refresh the page or try again in a moment.",
  ),
  icon: Icon = IconAlertTriangle,
  title = __("Something went wrong."),
  tone = "danger",
  visual = "icon",
  ...props
}: Omit<StateProps, "icon"> & { icon?: TablerIcon }) {
  return (
    <StateView
      icon={<Icon />}
      description={description}
      status="error"
      title={title}
      tone={tone}
      visual={visual}
      {...props}
    />
  );
}

export function InfoState({
  tone = "default",
  ...props
}: Omit<StateProps, "tone"> & { tone?: keyof typeof stateToneClassName }) {
  return <StateView status="info" tone={tone} {...props} />;
}

export function LoadingState({
  description = __("Please wait while the page content is prepared."),
  rows = 3,
  size = "section",
  surface,
  title = __("Loading page"),
  ...props
}: Omit<StateProps, "icon" | "tone" | "title" | "visual"> & {
  rows?: number;
  title?: ReactNode;
}) {
  const hasTextContent = title != null || description != null;

  return (
    <StateView
      icon={
        <LoadingStateVisual
          rows={rows}
          showRows={!hasTextContent}
          size={size}
        />
      }
      description={description}
      status="loading"
      size={size}
      surface={surface ?? (size === "page" ? "card" : "plain")}
      title={title}
      tone="default"
      visual="custom"
      {...props}
    />
  );
}

export function StateView({
  icon,
  status = "info",
  tone,
  visual,
  ...props
}: StateViewProps) {
  if (status === "loading") {
    const size = props.size || "section";
    const hasTextContent = props.title != null || props.description != null;

    return (
      <StateShell
        icon={
          icon || <LoadingStateVisual showRows={!hasTextContent} size={size} />
        }
        tone={tone || "default"}
        visual={visual || "custom"}
        {...props}
      />
    );
  }

  if (status === "empty") {
    return (
      <StateShell
        icon={icon || <IconInbox />}
        tone={tone || "muted"}
        visual={visual || "object"}
        {...props}
      />
    );
  }

  if (status === "error") {
    return (
      <StateShell
        icon={icon || <IconAlertTriangle />}
        tone={tone || "danger"}
        visual={visual || "icon"}
        {...props}
      />
    );
  }

  if (status === "success") {
    return (
      <StateShell
        icon={icon || <IconInfoCircle />}
        tone={tone || "success"}
        visual={visual || "icon"}
        {...props}
      />
    );
  }

  return (
    <StateShell
      icon={icon || <IconInfoCircle />}
      tone={tone || "default"}
      visual={visual || "icon"}
      {...props}
    />
  );
}
